import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config';
import { getUserProfile } from '../firebase/auth';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        setError(null);

        if (user) {
          setCurrentUser(user);

          // Get user profile from Firestore
          const { profile, error: profileError } = await getUserProfile(user.uid);

          if (profileError) {
            console.error('Error loading user profile:', profileError);
            setError(profileError);
            setUserProfile(null);
          } else {
            setUserProfile(profile);
          }
        } else {
          setCurrentUser(null);
          setUserProfile(null);
        }
      } catch (error) {
        console.error('Error in auth state change:', error);
        setError(error);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  // Function to refresh user profile
  const refreshProfile = async () => {
    if (currentUser) {
      try {
        const { profile, error: profileError } = await getUserProfile(currentUser.uid);
        if (!profileError && profile) {
          setUserProfile(profile);
        }
      } catch (error) {
        console.error('Error refreshing profile:', error);
      }
    }
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    error,
    isAuthenticated: !!currentUser,
    uid: currentUser?.uid || null,
    email: currentUser?.email || null,
    displayName: userProfile?.displayName || currentUser?.displayName || null,
    photoURL: userProfile?.photoURL || currentUser?.photoURL || null,
    emailVerified: currentUser?.emailVerified || false,
    refreshProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};